import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon, HomeIcon, BugAntIcon } from '@heroicons/react/24/outline';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: 'page' | 'component' | 'global';
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: number | null = null;

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    retryCount: 0,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error info
    this.setState({ errorInfo });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Send error to monitoring service (if implemented)
    this.logErrorToService(error, errorInfo);
  }

  private logErrorToService = (error: Error, _errorInfo: ErrorInfo) => {
    // In a real application, you would send this to a monitoring service
    // like Sentry, LogRocket, or a custom error tracking service
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: _errorInfo.componentStack,
      level: this.props.level || 'component',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // For now, we'll just log to console
    console.group('🔴 Error Boundary Report');
    console.error('Error:', errorData);
    console.groupEnd();

    // TODO: Implement actual error reporting service
    // Example: errorReportingService.report(errorData);
  };

  private handleRetry = () => {
    if (this.state.retryCount >= 3) {
      // Maximum retries reached, suggest page reload
      this.handlePageReload();
      return;
    }

    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  private handlePageReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private renderErrorUI = () => {
    const { level = 'component' } = this.props;
    const { error, retryCount } = this.state;
    const isMaxRetries = retryCount >= 3;

    // Custom fallback provided
    if (this.props.fallback) {
      return this.props.fallback;
    }

    // Component-level error (minimal UI)
    if (level === 'component') {
      return (
        <div className="flex items-center justify-center p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200">
          <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium">Something went wrong</p>
            <p className="text-xs opacity-75 mt-1">
              {error?.message || 'An unexpected error occurred'}
            </p>
          </div>
          <button
            onClick={this.handleRetry}
            className="ml-2 px-3 py-1 text-xs bg-red-100 dark:bg-red-800 hover:bg-red-200 dark:hover:bg-red-700 rounded transition-colors"
            disabled={isMaxRetries}
          >
            <ArrowPathIcon className="h-3 w-3" />
          </button>
        </div>
      );
    }

    // Page or global level error (full screen)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full space-y-6 text-center">
          {/* Error Icon */}
          <div className="flex justify-center">
            <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-3">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
          </div>

          {/* Error Message */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Oops! Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {level === 'global' 
                ? 'We encountered an unexpected error. Our team has been notified.'
                : 'This page encountered an error and couldn\'t load properly.'
              }
            </p>
            
            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded text-left text-xs">
                <summary className="cursor-pointer font-medium mb-2 flex items-center">
                  <BugAntIcon className="h-4 w-4 mr-2" />
                  Error Details (Development)
                </summary>
                <pre className="whitespace-pre-wrap break-words text-red-600 dark:text-red-400">
                  {error.message}
                  {error.stack && `\n\n${error.stack}`}
                </pre>
              </details>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {!isMaxRetries ? (
              <button
                onClick={this.handleRetry}
                className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Try Again {retryCount > 0 && `(${retryCount}/3)`}
              </button>
            ) : (
              <button
                onClick={this.handlePageReload}
                className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Reload Page
              </button>
            )}
            
            <button
              onClick={this.handleGoHome}
              className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <HomeIcon className="h-4 w-4 mr-2" />
              Go Home
            </button>
          </div>

          {/* Helpful Tips */}
          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
            <p>If this problem persists:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Try refreshing your browser</li>
              <li>Clear your browser cache</li>
              <li>Check your internet connection</li>
              <li>Contact support if the issue continues</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  public render() {
    if (this.state.hasError) {
      return this.renderErrorUI();
    }

    return this.props.children;
  }
}

// Convenience wrapper components for different levels
export const ComponentErrorBoundary: React.FC<Omit<Props, 'level'>> = (props) => (
  <ErrorBoundary {...props} level="component" />
);

export const PageErrorBoundary: React.FC<Omit<Props, 'level'>> = (props) => (
  <ErrorBoundary {...props} level="page" />
);

export const GlobalErrorBoundary: React.FC<Omit<Props, 'level'>> = (props) => (
  <ErrorBoundary {...props} level="global" />
);

export default ErrorBoundary;